import React, { useEffect, useState } from "react";
import { PageHeader } from "./Pengumuman";
import { Shield, Plus, Edit3, Trash2, User as UserIcon, Phone, Image as ImageIcon } from "lucide-react";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { satpamPersonelAPI } from "../lib/api";
import { useApp } from "../context/AppContext";
import { toast } from "sonner";

const emptyForm = { nama: "", kontak: "", group_key: "G1", foto: "", is_komandan: false };
const GROUP_COLOR = { G1: "bg-emerald-100 text-emerald-800 border-emerald-200", G2: "bg-blue-100 text-blue-800 border-blue-200", G3: "bg-amber-100 text-amber-800 border-amber-200" };
const GROUP_NAME = { G1: "GROUP 1", G2: "GROUP 2", G3: "GROUP 3" };

const IdentitasSatpam = () => {
  const { user } = useApp();
  const canEdit = user.role === "admin" || user.role === "super_admin";
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);

  const load = () => satpamPersonelAPI.list().then(setItems).catch(() => {});
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(emptyForm); setOpen(true); };
  const openEdit = (p) => { setEditing(p); setForm({ nama: p.nama, kontak: p.kontak || "", group_key: p.group_key || "G1", foto: p.foto || "", is_komandan: !!p.is_komandan }); setOpen(true); };

  const handleFile = (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    if (f.size > 2 * 1024 * 1024) { toast.error("Foto maks 2MB"); return; }
    const r = new FileReader(); r.onload = (ev) => setForm((s) => ({ ...s, foto: ev.target.result })); r.readAsDataURL(f);
  };

  const submit = async () => {
    if (!form.nama) { toast.error("Nama wajib diisi"); return; }
    try {
      if (editing) { await satpamPersonelAPI.update(editing.id, form); toast.success("Identitas diperbarui"); }
      else { await satpamPersonelAPI.create(form); toast.success("Personel ditambahkan"); }
      setOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal"); }
  };
  const remove = async (id) => {
    if (!window.confirm("Hapus personel ini?")) return;
    try { await satpamPersonelAPI.remove(id); toast.success("Dihapus"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Gagal"); }
  };

  const byGroup = { G1: [], G2: [], G3: [] };
  items.forEach((p) => { const k = p.group_key || "G1"; (byGroup[k] || byGroup.G1).push(p); });

  return (
    <div>
      <PageHeader title="Identitas Satpam" subtitle="Data personel satpam DM Residence — nama, kontak, group, dan foto" icon={Shield}
        action={canEdit && (
          <Dialog open={open} onOpenChange={setOpen}>
            <Button className="brand-btn" onClick={openNew}><Plus className="h-4 w-4 mr-1" />Tambah Personel</Button>
            <DialogContent>
              <DialogHeader><DialogTitle>{editing ? "Edit Personel" : "Tambah Personel"}</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div className="flex items-center gap-4">
                  {form.foto ? <img src={form.foto} alt="foto" className="h-24 w-24 rounded-full object-cover shadow border-2 border-emerald-100" /> : <div className="h-24 w-24 rounded-full bg-slate-100 grid place-items-center"><UserIcon className="h-10 w-10 text-slate-400" /></div>}
                  <div><input type="file" accept="image/*" id="satpam-foto" className="hidden" onChange={handleFile} /><Label htmlFor="satpam-foto" className="inline-block cursor-pointer px-4 py-2 rounded-lg brand-btn text-white text-sm font-semibold"><ImageIcon className="h-4 w-4 inline-block mr-1" />Unggah Foto</Label><div className="text-xs text-slate-500 mt-1">Maks 2MB, format bebas</div></div>
                </div>
                <div><Label>Nama</Label><Input value={form.nama} onChange={(e) => setForm({ ...form, nama: e.target.value })} placeholder="Contoh: Mang Iki" /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Nomor Kontak / WA</Label><Input value={form.kontak} onChange={(e) => setForm({ ...form, kontak: e.target.value })} placeholder="0812-xxxx-xxxx" /></div>
                  <div><Label>Group</Label><Select value={form.group_key} onValueChange={(v) => setForm({ ...form, group_key: v })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="G1">GROUP 1</SelectItem><SelectItem value="G2">GROUP 2</SelectItem><SelectItem value="G3">GROUP 3</SelectItem></SelectContent></Select></div>
                </div>
                <label className="flex items-center gap-2 text-sm text-slate-700">
                  <input type="checkbox" checked={!!form.is_komandan} onChange={(e) => setForm({ ...form, is_komandan: e.target.checked })} data-testid="personel-komandan-check" />
                  Jadikan <b>Komandan Regu</b> (mendapat tunjangan tambahan bulanan)
                </label>
              </div>
              <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Batal</Button><Button onClick={submit} className="brand-btn">{editing ? "Simpan" : "Tambah"}</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      />

      <div className="grid md:grid-cols-3 gap-4">
        {["G1", "G2", "G3"].map((k) => (
          <Card key={k} className={`border-2 ${k === "G1" ? "border-emerald-200" : k === "G2" ? "border-blue-200" : "border-amber-200"}`}>
            <CardContent className="p-5">
              <div className="flex items-center gap-2 mb-4">
                <div className={`h-9 w-9 rounded-lg grid place-items-center font-bold text-white ${k === "G1" ? "bg-emerald-600" : k === "G2" ? "bg-blue-600" : "bg-amber-500"}`}>{k}</div>
                <div className="font-bold text-slate-800">{GROUP_NAME[k]}</div>
                <Badge variant="secondary" className="ml-auto">{byGroup[k].length} org</Badge>
              </div>
              <div className="space-y-2">
                {byGroup[k].map((p) => (
                  <div key={p.id} className="flex items-center gap-3 p-3 rounded-lg bg-slate-50 border border-slate-100">
                    {p.foto ? <img src={p.foto} alt={p.nama} className="h-12 w-12 rounded-full object-cover ring-2 ring-white shadow" /> : <div className="h-12 w-12 rounded-full bg-slate-200 grid place-items-center"><UserIcon className="h-6 w-6 text-slate-400" /></div>}
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-slate-800 truncate">{p.nama} {p.is_komandan && <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100 text-[9px] ml-1" data-testid={`badge-komandan-${p.nama.replace(/\s+/g, "-")}`}>KOMANDAN</Badge>}</div>
                      {p.kontak && <a href={`tel:${p.kontak.replace(/[^0-9]/g, "")}`} className="text-xs text-emerald-700 flex items-center gap-1 font-mono"><Phone className="h-3 w-3" />{p.kontak}</a>}
                    </div>
                    {canEdit && <div className="flex flex-col"><Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => openEdit(p)}><Edit3 className="h-3.5 w-3.5" /></Button>{user.role === "super_admin" && <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-600" onClick={() => remove(p.id)}><Trash2 className="h-3.5 w-3.5" /></Button>}</div>}
                  </div>
                ))}
                {byGroup[k].length === 0 && <div className="text-center py-4 text-slate-400 text-xs italic">Belum ada personel di grup ini</div>}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};
export default IdentitasSatpam;
